import type { LayeredEngravingCatalog, LayeredEngravingSelection } from "~/shared/layeredEngraving";
export type ShapeType =
  | "rect"
  | "rect-triangle"
  | "arc"
  | "hex"
  | "diamond"
  | "triangle";
export type GridPatternType =
  | "square"
  | "45-deg"
  | "hex"
  | "hex-90"
  | "freeform"
  | "hex-spaced"
  | "3d";

// todo: improve shape's structure
// avoiding attrs repeating attributes from its parent
// spliting metadata/business and drawing-related data
interface BaseShape {
  id: string;
  type: ShapeType;
  x: number;
  y: number;
  rotation: number;
  draggable?: boolean;
  color?: string;
  attrs?: any;
  metadata?: ShapeMetadata;
  offsetX?: number;
  offsetY?: number;
}

interface Sizeable {
  width?: number;
  height?: number;
}

interface Fillable {
  fillPatternImage: HTMLImageElement;
  fillPatternRotation: number;
  fillPatternRepeat?: "repeat" | "no-repeat";
  fillPatternOffset?: { x: number; y: number };
  fillPatternScale?: { x: number; y: number };
}

interface Selectable {
  stroke?: string;
  strokeWidth?: number;
  dash?: number[];
  dashOffset?: number;
  hitStrokeWidth?: number | "auto";
}

export type Rect = BaseShape & Sizeable & Fillable & Selectable;
export interface Line extends BaseShape, Sizeable, Fillable, Selectable {
  tension: number;
  closed: boolean;
  points: number[];
}
export interface Arc extends BaseShape, Sizeable, Fillable, Selectable {
  innerRadius: number;
  angle: number;
  outerRadius: number;
}
export interface RegPolygon extends BaseShape, Sizeable, Fillable, Selectable {
  sides: number;
  radius: number;
}
export interface ThreeDimensional
  extends BaseShape, Sizeable, Fillable, Selectable {
  z: number; // For 3D shapes
  quantity?: number; // Number of items in the set
}
export type ShapeInterface = Rect | Line | Arc | RegPolygon;

export interface Vector2d {
  x: number;
  y: number;
}

export interface ShapeClientRect extends Vector2d {
  width: number;
  height: number;
}

export interface Color {
  image: string;
  image_3d: string;
  label: string;
  sample: string;
}

// todo: Ensure standard to snake_case or camelCase between front and back
export interface ShapeMetadata {
  layered_engraving?: LayeredEngravingSelection;
  variant_id: string;
  product_id: string;
  price: number;
  photo_url: string;
  shape_id: string;
  shape_label: string;
  color_label: string;
  quantity?: number;
  legend_id?: string;
  handle?: string;
  depth?: number;
  set_id?: string; // ID to group items that were added together as a set
  rotation_locked?: boolean;
  physical_width_in?: number;
  physical_height_in?: number;
  depth_mm?: number;
}

export interface UpdatableShapeAttrs {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  fill: string;
  opacity: number;
  rotation: number;
  fillPatternImage: any;
  blankFillPatternImage: any;
  attrs: any;
  metadata: any;
}

// todo: it was supposed to work with a dummy object like {} as UpdatableShapeAttrs
export const mockUpdatableShapeAttrs: UpdatableShapeAttrs = {
  x: 20,
  y: 20,
  scaleX: 1,
  scaleY: 1,
  fill: "",
  opacity: 1,
  rotation: 0,
  fillPatternImage: null,
  blankFillPatternImage: null,
  attrs: {},
  metadata: {},
};

export interface PatternSquareConfig {
  bgColor: string;
  width: number;
  height: number;
  padding: number;
  footInPx: number;
  rulerSize: number;
}

export enum CollisionLevel {
  ALL = "all",
  NONE = "none",
  NEW_OR_COPY = "new_or_copy",
}

export interface GridPattern {
  id: GridPatternType;
  label: string;
  image: string;
  padding: Vector2d; // same as tile size
  snap: Vector2d; // where can be snapped, usually half of the tile size
  fineSnap?: Vector2d; // 1-inch placement step; patterns without one place on snap
  active: boolean;
  collisionLevel: CollisionLevel;
}

export interface GridClipboard {
  action: "copy" | "cut" | null;
  items: Array<ShapeInterface>;
}

// todo: split Shape interfaces for menu, canvas and database purposes
export type Shape = {
  type: ShapeType;
  id: string;
  icon: string;
  prefix: ShapePrefix;
  label: string;
  w: number;
  h: number;
  d?: number;
  rotation?: number;
  imgRotation?: number;
  radius?: number;
  angle?: number;
  sides?: number;
  children?: Shape[];
  menuAlign?: "left" | "right";
  admin?: boolean;
  quantity?: number;
  rotationLocked?: boolean;
  finishFamily?: "wood-slat" | "layered-engraved";
  allowedColors?: string[];
  productId?: string;
  physicalWidthIn?: number;
  physicalHeightIn?: number;
  depthMm?: number;
};

export type ShapeGroup = {
  title: string;
  items: (Shape | { sectionSubtitle: string } | { breakpoint: boolean })[];
  collapse?: boolean;
};

export interface GridShapesMenu {
  list: ShapeGroup[];
  default?: Shape;
}

export type ShapePrefix =
  | `Layered${string}`
  | "LargeHalfCircle"
  | "QuarterCircle"
  | "HalfCircle"
  | "SmallHalfCircle"
  | "LargeSquare"
  | "Quarter"
  | "Pixel"
  | "Bit"
  | "LargeHalflap"
  | "Half"
  | "MiniShiplap"
  | "Shiplap"
  | "LargeTriangle"
  | "Small90Triangle"
  | "SmallTriangle"
  | "Triangle"
  | "Tangent"
  | "CrissCross"
  | "Knot"
  | "Converge"
  | "Topo"
  | "Offset"
  | "Arc"
  | "Hex"
  | "LargeDiamond"
  | "Diamond"
  | "Blank"
  | "Slash"
  | "Cross"
  | "4Square"
  | "LargeParallel"
  | "Parallel"
  | "River"
  | "Subway"
  | "Zen"
  // Printed 2'x2' Tile Prefixes
  | "Aspen"
  | "Bamboo"
  | "Geo"
  | "Pinstripe"
  | "Blossom"
  | "Strand"
  // Peak Tile Prefixes
  | "SmallPeak"
  | "LargeCenterPeak"
  | "LargeUpperPeak"
  | "WoodSlat4Ft"
  | "WoodSlat8Ft"
  // 3D Tile Prefixes
  | "Cube36x36x2"
  | "Cube36x36x4"
  | "Cube12x12x2"
  | "Cube12x12x4"
  | "Rect12x36x2"
  | "Rect12x36x4"
  // 3D Engraved Tile Prefixes
  | "Cube36x36x4LargeParallel"
  | "Cube36x36x2LargeParallel"
  | "Cube36x36x4Tangent"
  | "Cube36x36x2Tangent"
  | "Cube36x36x4Zen"
  | "Cube36x36x2Zen"
  | "Cube36x36x4River"
  | "Cube36x36x2River"
  | "Cube36x36x4Offset"
  | "Cube36x36x2Offset"
  | "Cube36x36x4Topo"
  | "Cube36x36x2Topo"
  | "Cube36x36x4Converge"
  | "Cube36x36x2Converge"
  | "Cube36x36x4Subway"
  | "Cube36x36x2Subway";

export interface Variation {
  layered_engraving?: LayeredEngravingCatalog;
  shape_key?: string | null;
  studio_shape?: import('~/shared/slats').SlatShape | null;
  label: string;
  photo: string;
  price: number;
  product_id: string;
  total_tabs: number;
  variant_id: string;
  handle?: string;
  /**
   * Degrees to rotate the tile image so it fits its shape on the canvas.
   * Null/undefined means fall back to the shape definition's imgRotation.
   */
  image_rotation?: number | null;
  /** Saved zoom/pan for the tile image; null means the default fit. */
  image_placement?: { zoom: number; offsetX: number; offsetY: number } | null;
}

export interface LegacyTile {
  id?: string;
  x: number;
  y: number;
  type?: string;
  pattern: {
    label: string;
    name?: string;
  };
  shape?:
    | {
        name?: string;
        label: string;
        modifier?: string;
        patterns?: string[];
        rotate?: number;
      }
    | string
    | any;
  customRotation?: number;
  color: {
    image?: string;
    label?: string;
  };
}
